import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, FileText, DollarSign, Building, CheckCircle, XCircle, Eye, Search, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import StatistiquesAdmin from '../components/StatistiquesAdmin';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminGestion() {
  const { getAuthHeader } = useAuth();
  
  // States
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [_members, _setMembers] = useState([]);
  const [coopers, setCoopers] = useState([]);
  const [dispositifs, setDispositifs] = useState([]);
  const [_souscriptions, _setSouscriptions] = useState([]);
  const [paiements, setPaiements] = useState([]);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, coopersRes, dispositifsRes, paiementsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/dispositifs`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/paiements/admin/all`, { headers: getAuthHeader() }).catch(() => ({ data: { paiements: [] } }))
      ]);
      
      // Trier du plus récent au plus ancien
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      const usersData = usersRes.data?.users || usersRes.data || [];
      const coopersData = coopersRes.data?.coopers || coopersRes.data || [];
      setUsers(sortByDate(Array.isArray(usersData) ? usersData : []));
      setCoopers(sortByDate(Array.isArray(coopersData) ? coopersData : []));
      setDispositifs(sortByDate(Array.isArray(dispositifsRes.data) ? dispositifsRes.data : []));
      setPaiements(sortByDate(Array.isArray(paiementsRes.data?.paiements) ? paiementsRes.data.paiements : []));
      
      // Calculer statistiques
      const statsData = {
        totalUsers: usersData?.length || 0,
        totalCoopers: coopersData?.length || 0,
        totalDispositifs: dispositifsRes.data?.length || 0,
        paiementsEnAttente: (paiementsRes.data?.paiements || []).filter(p => p.statut === 'en_attente').length,
        paiementsValides: (paiementsRes.data?.paiements || []).filter(p => p.statut === 'valide').length,
        montantTotal: (paiementsRes.data?.paiements || [])
          .filter(p => p.statut === 'valide')
          .reduce((sum, p) => sum + (p.montant || 0), 0)
      };
      setStats(statsData);
      
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePaiement = async (paiementId, statut, commentaire = '') => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/paiements/${paiementId}/valider`,
        { statut, commentaire_admin: commentaire },
        { headers: getAuthHeader() }
      );
      alert(statut === 'valide' ? 'Paiement validé !' : 'Paiement rejeté');
      fetchData();
    } catch {
      alert('Erreur lors de la validation');
    }
  };

  const handleBlockUser = async (userId, isBlocked) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${userId}/block`,
        { is_blocked: !isBlocked },
        { headers: getAuthHeader() }
      );
      alert(isBlocked ? 'Utilisateur débloqué' : 'Utilisateur bloqué');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors du blocage';
      alert(errorMessage);
    }
  };

  // Filtrage utilisateurs
  const filteredUsers = (Array.isArray(users) ? users : []).filter(user => {
    const matchSearch = !searchQuery || 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.prenom?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchRole = filterRole === 'all' || user.role === filterRole;
    const matchStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && !user.is_blocked) ||
      (filterStatus === 'blocked' && user.is_blocked);
    
    return matchSearch && matchRole && matchStatus;
  });

  // Filtrage paiements
  const filteredPaiements = (Array.isArray(paiements) ? paiements : []).filter(p => {
    return !searchQuery || 
      p.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.member_nom?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Bouton Retour */}
      <div className="mb-4">
        <BackButton to="/" label="Accueil" />
      </div>
      
      {/* En-tête */}
      <div>
        <h1 className="text-3xl font-bold">Administration Générale</h1>
        <p className="text-gray-600">Gestion complète de la plateforme</p>
      </div>

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Utilisateurs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalUsers}</div>
            <p className="text-xs text-gray-500 mt-1">Total inscrits</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              COOPER'S
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalCoopers}</div>
            <p className="text-xs text-gray-500 mt-1">Partenaires réseau ESS actifs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Dispositifs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalDispositifs}</div>
            <p className="text-xs text-gray-500 mt-1">Offres disponibles</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Revenus
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.montantTotal?.toFixed(0)}€</div>
            <p className="text-xs text-gray-500 mt-1">Paiements validés</p>
          </CardContent>
        </Card>
      </div>

      {/* Onglets de gestion */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="statistiques">
            <TrendingUp className="h-4 w-4 mr-2" />
            Statistiques
          </TabsTrigger>
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="coopers">COOPER'S</TabsTrigger>
          <TabsTrigger value="dispositifs">Dispositifs</TabsTrigger>
          <TabsTrigger value="paiements">Paiements</TabsTrigger>
        </TabsList>

        {/* Onglet Statistiques */}
        <TabsContent value="statistiques" className="space-y-4">
          <StatistiquesAdmin getAuthHeader={getAuthHeader} />
        </TabsContent>

        {/* Vue d'ensemble */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Paiements en attente</CardTitle>
                <CardDescription>Nécessitent une validation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-orange-600">
                  {stats.paiementsEnAttente}
                </div>
                <Button 
                  className="mt-4" 
                  onClick={() => setActiveTab('paiements')}
                >
                  Voir les paiements
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>Accès aux fonctionnalités principales</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('users')}>
                  <Users className="h-4 w-4 mr-2" />
                  Gérer les utilisateurs
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setActiveTab('dispositifs')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Gérer les dispositifs
                </Button>
                <Button variant="outline" className="w-full justify-start bg-green-50 border-green-200 hover:bg-green-100" onClick={() => window.location.href = '/admin/activity-billing'}>
                  <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                  <span className="text-green-600 font-semibold">Modèles Économiques Multi-Activités</span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Gestion utilisateurs */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Gestion des Utilisateurs</CardTitle>
              <CardDescription>Liste de tous les utilisateurs de la plateforme</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtres */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>Rechercher</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Email, nom..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="cooper">COOPER'S</SelectItem>
                      <SelectItem value="member">Membre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="active">Actifs</SelectItem>
                      <SelectItem value="blocked">Bloqués</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tableau utilisateurs */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Nom</th>
                      <th className="text-left p-3">Rôle</th>
                      <th className="text-left p-3">Statut</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center p-8 text-gray-500">
                          Aucun utilisateur trouvé
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{user.email}</td>
                          <td className="p-3">{user.nom} {user.prenom}</td>
                          <td className="p-3">
                            <Badge variant={
                              user.role === 'admin' ? 'destructive' : 
                              user.role === 'cooper' ? 'default' : 
                              'secondary'
                            }>
                              {user.role}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {user.is_blocked ? (
                              <Badge variant="destructive">Bloqué</Badge>
                            ) : (
                              <Badge className="bg-green-700 text-white font-semibold">Actif</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {user.role === 'super_admin' ? (
                              <Badge className="bg-purple-700 text-white text-xs font-semibold">Super Admin</Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant={user.is_blocked ? "default" : "destructive"}
                                onClick={() => handleBlockUser(user.id, user.is_blocked)}
                              >
                                {user.is_blocked ? 'Débloquer' : 'Bloquer'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gestion COOPER'S */}
        <TabsContent value="coopers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Gestion des COOPER'S</CardTitle>
              <CardDescription>Liste des partenaires réseau ESS</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {coopers.length === 0 ? (
                  <p className="text-center text-gray-500 py-4 md:py-8">Aucun COOPER'S enregistré</p>
                ) : (
                  coopers.map((cooper) => (
                    <Card key={cooper.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{cooper.nom} {cooper.prenom}</div>
                            <div className="text-sm text-gray-500">{cooper.entreprise}</div>
                            <div className="text-sm text-gray-500">{cooper.email}</div>
                          </div>
                          <div className="text-right">
                            {cooper.is_validated ? (
                              <Badge className="bg-green-700 text-white font-semibold">Validé</Badge>
                            ) : (
                              <Badge variant="destructive">En attente</Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gestion Dispositifs */}
        <TabsContent value="dispositifs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Gestion des Dispositifs Solidaires</CardTitle>
              <CardDescription>Offres d'assurance disponibles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dispositifs.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 col-span-2">Aucun dispositif disponible</p>
                ) : (
                  dispositifs.map((disp) => (
                    <Card key={disp.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{disp.nom}</CardTitle>
                        <CardDescription>{disp.type}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Tarif de base:</span>
                            <span className="font-semibold">{disp.tarif_base}€</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Validité:</span>
                            <span className="font-semibold">{disp.validite_jours} jours</span>
                          </div>
                          {disp.is_active ? (
                            <Badge className="bg-green-700 text-white font-semibold">Actif</Badge>
                          ) : (
                            <Badge variant="destructive">Inactif</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gestion Paiements */}
        <TabsContent value="paiements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Gestion des Paiements</CardTitle>
              <CardDescription>Validation des virements bancaires</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label>Rechercher</Label>
                <Input
                  placeholder="Référence, nom membre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Référence</th>
                      <th className="text-left p-3">Membre</th>
                      <th className="text-left p-3">Montant</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Statut</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPaiements.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center p-8 text-gray-500">
                          Aucun paiement trouvé
                        </td>
                      </tr>
                    ) : (
                      filteredPaiements.map((paiement) => (
                        <tr key={paiement.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-mono text-sm">{paiement.reference}</td>
                          <td className="p-3">
                            <div>{paiement.member_nom} {paiement.member_prenom}</div>
                            <div className="text-xs text-gray-500">{paiement.member_email}</div>
                          </td>
                          <td className="p-3 font-semibold">{paiement.montant}€</td>
                          <td className="p-3">
                            <Badge className="bg-blue-700 text-white font-semibold">{paiement.type_paiement}</Badge>
                          </td>
                          <td className="p-3">
                            {paiement.statut === 'en_attente' && (
                              <Badge className="bg-orange-700 text-white font-semibold">En attente</Badge>
                            )}
                            {paiement.statut === 'valide' && (
                              <Badge className="bg-green-700 text-white font-semibold">Validé</Badge>
                            )}
                            {paiement.statut === 'rejete' && (
                              <Badge variant="destructive">Rejeté</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {paiement.statut === 'en_attente' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600"
                                  onClick={() => handleValidatePaiement(paiement.id, 'valide')}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleValidatePaiement(paiement.id, 'rejete', 'Justificatif invalide')}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            {paiement.justificatif_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`${BACKEND_URL}${paiement.justificatif_url}`, '_blank')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
